import React, { useState, useEffect } from 'react';
import api from '../api';

const VehicleInsights = ({ vehicle, onStatusChange }) => {
  const [partner, setPartner] = useState(null);
  const [odometerReadings, setOdometerReadings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [kmRun, setKmRun] = useState(null);
  const [weeklyPayment, setWeeklyPayment] = useState(null);
  const [displayReading, setDisplayReading] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [chargingData, setChargingData] = useState(null);

  useEffect(() => {
    const fetchAllInsights = async () => {
      if (!vehicle || !vehicle.id) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setPartner(null);
      setOdometerReadings([]);
      setTransactions([]);
      setKmRun(null);
      setWeeklyPayment(null);
      setDisplayReading(null);

      const promises = [
        api.get(`/vehicles/vehicle/${vehicle.id}/partner`),
        api.get(`/vehicles/${vehicle.id}/odometer-readings`),
        api.get(`/vehicles/${vehicle.id}/transactions`),
      ];

      if (vehicle.chassis_number) {
        promises.push(api.get(`/vehicles/${vehicle.chassis_number}/km-run`));
        promises.push(api.get(`/vehicles/${vehicle.chassis_number}/weekly-payment?sendMessage=false`));
        promises.push(api.get(`/vehicles/${vehicle.chassis_number}/display-reading`));
      }

      const results = await Promise.allSettled(promises);

      const [
        partnerResult,
        odometerResult,
        transactionsResult,
        kmRunResult,
        weeklyPaymentResult,
        displayReadingResult,
      ] = results;

      if (partnerResult.status === 'fulfilled' && partnerResult.value.data && typeof partnerResult.value.data === 'object') {
        setPartner(partnerResult.value.data);
      } else {
        console.error('Failed to fetch partner details:', partnerResult.reason);
      }

      if (odometerResult.status === 'fulfilled' && Array.isArray(odometerResult.value.data)) {
        setOdometerReadings(odometerResult.value.data);
      } else {
        console.error('Failed to fetch odometer readings:', odometerResult.reason);
      }

      if (transactionsResult.status === 'fulfilled' && Array.isArray(transactionsResult.value.data)) {
        setTransactions(transactionsResult.value.data);
      } else {
        console.error('Failed to fetch transactions:', transactionsResult.reason);
      }

      if (kmRunResult?.status === 'fulfilled' && kmRunResult.value.data) {
        const rawKmRun = kmRunResult.value.data.km_run;
        const kmValue = typeof rawKmRun === 'object' && rawKmRun !== null ? Number(rawKmRun.km_run) : Number(rawKmRun);
        setKmRun(isNaN(kmValue) ? null : kmValue);
      } else {
        console.error('Failed to fetch kilometer run:', kmRunResult?.reason);
      }

      if (weeklyPaymentResult?.status === 'fulfilled' && weeklyPaymentResult.value.data && typeof weeklyPaymentResult.value.data === 'object') {
        setWeeklyPayment(weeklyPaymentResult.value.data);
      } else {
        console.error('Failed to fetch weekly payment:', weeklyPaymentResult?.reason);
      }

      if (displayReadingResult?.status === 'fulfilled' && displayReadingResult.value.data && typeof displayReadingResult.value.data === 'object') {
        setDisplayReading(displayReadingResult.value.data);
      } else {
        console.error('Failed to fetch display reading:', displayReadingResult?.reason);
      }

      setIsLoading(false);
    };

    fetchAllInsights();
  }, [vehicle]);

  useEffect(() => {
    if (!chargingData) return;
    const handleGlobalClick = () => setChargingData(null);
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, [chargingData]);

  const handleViewStatus = async () => {
    try {
      const res = await api.get(`/charging-stats?vehicleNo=${vehicle.chassis_number}`);
      setChargingData(res.data);
    } catch (error) {
      console.error('Error fetching charging details:', error);
      alert('Failed to load charging details.');
    }
  };

  const handleToggleStatus = async () => {
    try {
      const newStatus = vehicle.status === 'Active' ? 'Inactive' : 'Active';
      await api.patch(`/vehicles/vehicle/${vehicle.id}/toggle-status`, { status: newStatus });
      alert(`Vehicle marked as ${newStatus}!`);
      if (onStatusChange) onStatusChange();
    } catch (error) {
      console.error('Error toggling vehicle status:', error);
      alert('Error updating vehicle status.');
    }
  };

  return (
    <div className="p-6 bg-white dark:bg-gray-800 h-full overflow-y-auto">
      <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-6">
        {vehicle.vehicle_no} Insights
      </h2>
      <div className="space-y-6">
        <InsightCard title="Vehicle Details">
          <p><strong>Status:</strong> {vehicle.status}</p>
          <p><strong>Hub Tag:</strong> {vehicle.hub_tag || 'N/A'}</p>
          <p><strong>Onboarding Date:</strong> {new Date(vehicle.onboarding_date).toLocaleDateString()}</p>
          <button onClick={handleViewStatus} className="mt-3 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">
            View Status
          </button>
          <button onClick={handleToggleStatus} className={`mt-3 ml-3 ${vehicle.status === 'Active' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white font-bold py-2 px-4 rounded-lg`}>
            {vehicle.status === 'Active' ? 'Mark as Inactive' : 'Mark as Active'}
          </button>
        </InsightCard>

        {chargingData && <ChargingPopup details={chargingData} onClose={() => setChargingData(null)} vehicle={vehicle} />}

        <InsightCard title="Partner Details">
          {isLoading ? <p>Loading...</p> : partner ? (
            <div>
              <p><strong>Name:</strong> {partner.partner_name}</p>
              <p><strong>Number:</strong> {partner.partner_number}</p>
            </div>
          ) : <p className="text-gray-500">No partner assigned.</p>}
        </InsightCard>

        <InsightCard title="Weekly Payment">
          {isLoading ? <p>Loading...</p> : weeklyPayment ? (
            <div className="space-y-1">
              <p className="flex justify-between"><span>Week:</span><span className="font-semibold">{new Date(weeklyPayment.week_start_date).toLocaleDateString()} - {new Date(weeklyPayment.week_end_date).toLocaleDateString()}</span></p>
              <p className="flex justify-between"><span>KM Run (Week):</span><span className="font-semibold">{parseFloat(weeklyPayment.total_km).toFixed(2)} km</span></p>
              <p className="flex justify-between text-lg mt-2 pt-2 border-t"><span className="font-bold">Total Amount Due:</span><span className="font-bold text-red-600">₹{parseFloat(weeklyPayment.total_amount).toFixed(2)}</span></p>
            </div>
          ) : <p className="text-gray-500">No weekly payment data available.</p>}
        </InsightCard>

        <InsightCard title="Odometer Readings">
          <div className="space-y-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="flex justify-between items-center">
                <span className="font-semibold text-blue-800">
                  {displayReading && new Date(displayReading.reading_date).toDateString() === new Date().toDateString() ? "Today's Run" : 'Last Recorded Run'}
                </span>
                {isLoading ? <span className="text-sm">...</span> : <span className="font-bold text-lg text-blue-600">{displayReading && displayReading.km_run != null ? `${parseFloat(displayReading.km_run).toFixed(2)} km` : 'N/A'}</span>}
              </p>
              {displayReading && <p className="text-xs text-gray-500 text-right">({new Date(displayReading.reading_date).toLocaleDateString()})</p>}
            </div>
            <p className="flex justify-between pt-2 border-t">
              <span className="font-semibold">Lifetime Total:</span>
              <span className="font-semibold">{typeof kmRun === 'number' ? `${kmRun.toFixed(2)} km` : 'N/A'}</span>
            </p>
          </div>
        </InsightCard>

        <InsightCard title="Transactions">
          <ul className="space-y-2">
            {transactions.length > 0 ? (
              transactions.map((transaction) => (
                <li key={transaction.id} className="flex justify-between">
                  <span>{new Date(transaction.collection_date).toLocaleDateString()}:</span>
                  <span className="font-semibold text-green-600">₹{transaction.amt_received}</span>
                </li>
              ))
            ) : <p className="text-gray-500">No transactions found.</p>}
          </ul>
        </InsightCard>
      </div>
    </div>
  );
};

const ChargingPopup = ({ details, onClose, vehicle }) => {
  if (!details) return null;
  const { latitude, longitude, batteryInfo, locationLastUpdatedTimestamp, ignitionStatus, odoMeterReadingInKM } = details;
  const getGoogleMapsUrl = (lat, lng) => `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-lg w-[500px] max-w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-2xl font-bold mb-4">Vehicle Charging Details</h3>
        <div className="mt-4 space-y-2">
          <p><strong>Vehicle No:</strong> {vehicle?.vehicle_no}</p>
          <p><strong>Ignition Status:</strong> {ignitionStatus}</p>
          <p><strong>Odometer:</strong> {odoMeterReadingInKM} km</p>
          <p><strong>Charging Status:</strong> {batteryInfo?.chargingStatus}</p>
          <p><strong>Charging Mode:</strong> {batteryInfo?.typeOfCharge || 'N/A'}</p>
          <p><strong>Current SoC:</strong> {batteryInfo?.soc ? `${batteryInfo.soc.toFixed(2)}%` : 'N/A'}</p>
          <p><strong>Updated On:</strong> {locationLastUpdatedTimestamp ? new Date(locationLastUpdatedTimestamp).toLocaleString() : 'N/A'}</p>
          {latitude && longitude && (
            <div className="flex gap-2 mt-2">
              <button onClick={() => window.open(getGoogleMapsUrl(latitude, longitude), '_blank')} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">Open in Maps</button>
            </div>
          )}
        </div>
        <button onClick={onClose} className="mt-6 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg">Close</button>
      </div>
    </div>
  );
};

const InsightCard = ({ title, children }) => (
  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 shadow-sm">
    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-3">{title}</h3>
    <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">{children}</div>
  </div>
);

export default VehicleInsights;